import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { MessageSquare, X } from "lucide-react";

const EMPTY = { name: "", contact: "", email: "", message: "" };

export default function InquiryFormDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/inquiries", {
        name: form.name,
        contact: form.contact,
        email: form.email || undefined,
        message: form.message,
      });
      setSent(true);
      toast.success("Message sent — we'll get back to you soon");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    setForm(EMPTY);
    setSent(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent
        className="max-w-lg border-2 border-[#0A0A0A] shadow-brutal rounded-xl bg-white"
        data-testid="inquiry-dialog"
      >
        {sent ? (
          <div className="text-center py-8" data-testid="inquiry-success">
            <div className="inline-block px-3 py-1 border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white text-xs font-bold uppercase tracking-[0.2em]">
              Message received
            </div>
            <h3 className="font-heading font-black text-3xl tracking-tighter mt-4">
              Thanks for reaching out!
            </h3>
            <p className="mt-3 font-medium text-[#4A4A4A]">
              Our team will get back to you shortly. You can also call the wardens directly for urgent queries.
            </p>
            <button
              onClick={close}
              className="mt-6 inline-flex items-center gap-2 font-bold px-5 py-2.5 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading font-black text-3xl tracking-tighter">
                Send us a message
              </DialogTitle>
              <DialogDescription className="text-sm font-medium text-[#4A4A4A]">
                Ask about rooms, food, admission or plan a visit. Our team responds within a few hours.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2" data-testid="inquiry-form">
              <Field label="Your name" required>
                <input
                  data-testid="inquiry-name"
                  required
                  value={form.name}
                  onChange={(e) => change("name", e.target.value)}
                  className="input-brutal"
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Phone" required>
                  <input
                    data-testid="inquiry-contact"
                    required
                    value={form.contact}
                    onChange={(e) => change("contact", e.target.value)}
                    className="input-brutal"
                  />
                </Field>
                <Field label="Email (optional)">
                  <input
                    data-testid="inquiry-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => change("email", e.target.value)}
                    className="input-brutal"
                  />
                </Field>
              </div>
              <Field label="Message" required>
                <textarea
                  data-testid="inquiry-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => change("message", e.target.value)}
                  className="input-brutal"
                  placeholder="Tell us what you'd like to know..."
                />
              </Field>
              <DialogFooter className="gap-3">
                <button
                  type="button"
                  onClick={close}
                  className="px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="inquiry-submit"
                  className="inline-flex items-center gap-2 px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60"
                >
                  <MessageSquare size={14} />
                  {saving ? "Sending..." : "Send message"}
                </button>
              </DialogFooter>
            </form>
          </>
        )}
        <style>{`
          .input-brutal {
            width: 100%;
            padding: 0.7rem 1rem;
            border: 2px solid #0A0A0A;
            border-radius: 0.75rem;
            background: white;
            font-weight: 500;
          }
          .input-brutal:focus {
            outline: none;
            box-shadow: 0 0 0 2px white, 0 0 0 4px #0A0A0A;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">
        {label} {required && <span className="text-[#FF4D4D]">*</span>}
      </span>
      {children}
    </label>
  );
}
